import { Form, Input, Menu } from "antd";
import { searchPatientMenu } from "./SearchPatientMenu/SearchPatientMenu";
import { usePatientQuery } from "api/clients/usePatientQuery";
import { useState } from "react";
import type { ExistingPatientResult } from "api/responses";

interface SearchPatientFieldProps {
    /** Callback to execute when an existing P/RP is selected. */
    readonly onSelectExisting: (selected: ExistingPatientResult) => void;

    /** If set, in Mason-integrated sessions, P/RPs not linked to PMS patients are excluded. */
    readonly excludeUnlinked?: boolean;

    /** Indicates an intent to show the search result dropdown, due to the field being actually or recently focused.
     * This intent will be honored only when enough characters have been entered into the field. */
    readonly active: boolean;

    /** Callback to execute when the field gains focus. */
    readonly onFocus: () => void;
}

/** Field that will search across Patients/Responsible Parties and return results in a menu. */
export const SearchPatientField = ({ onSelectExisting, excludeUnlinked, active, onFocus }: SearchPatientFieldProps) => {
    const { searchFilter, setSearchFilter, isFilterValid, validateStatus, help, menu } = useSearchPatientField(
        onSelectExisting,
        excludeUnlinked,
    );

    return (
        <>
            <div className={"vyne-search-patient-wrap" + (isFilterValid ? " vyne-search-filter-valid" : "")}>
                <Form.Item
                    label="Search for Patient/Guarantor"
                    name="searchPatientFullName"
                    validateStatus={validateStatus}
                    help={help}
                    data-testid="queryFullName"
                >
                    <Input.Search
                        autoComplete="off"
                        type="search"
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        onFocus={onFocus}
                    />
                </Form.Item>
            </div>
            {isFilterValid && active && <Menu mode="inline" className="vyne-search-patient-menu" {...menu} />}
        </>
    );
};

function useSearchPatientField(
    onSelectExisting: (selected: ExistingPatientResult) => void,
    excludeUnlinked: boolean | undefined,
) {
    const [searchFilter, setSearchFilter] = useState("");
    const { isFilterValid, isFetching, data } = usePatientQuery(searchFilter, excludeUnlinked ?? false, 200);

    // display error only in steady state
    const validateStatus = searchFilter && !isFetching && !isFilterValid ? "error" : undefined;
    const help = validateStatus ? "Searching requires at least 3 characters" : undefined;

    const menu = searchPatientMenu(isFetching, data, onSelectExisting);

    return { searchFilter, setSearchFilter, isFilterValid, validateStatus, help, menu } as const;
}
